import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import PageSEO from "@/components/feature/PageSEO";

const values = [
  { icon: "ri-shield-check-line", title: "Honestidad", desc: "Presupuestos claros, sin costes ocultos ni sorpresas al final del trabajo." },
  { icon: "ri-eye-off-line", title: "Discreción", desc: "Respetamos la intimidad de cada cliente, especialmente en situaciones delicadas." },
  { icon: "ri-recycle-line", title: "Responsabilidad", desc: "Gestionamos los residuos de forma sostenible y cumpliendo la normativa vigente." },
  { icon: "ri-heart-line", title: "Empatía", desc: "Entendemos que detrás de cada vaciado hay una historia. Actuamos con respeto y sensibilidad." },
];

const steps = [
  { num: "01", title: "Contacto y valoración", desc: "Nos llamas o escribes por WhatsApp. Valoramos el trabajo por fotos o con una visita gratuita sin compromiso." },
  { num: "02", title: "Presupuesto detallado", desc: "Te enviamos un presupuesto claro y detallado. Sin letra pequeña. Tú decides si seguimos adelante." },
  { num: "03", title: "Planificación del trabajo", desc: "Acordamos fecha y horario que te convenga. Coordinamos el acceso al inmueble con total discreción." },
  { num: "04", title: "Vaciado y limpieza", desc: "Nuestro equipo trabaja de forma eficiente y ordenada. Retiramos todo lo acordado y dejamos el espacio limpio." },
  { num: "05", title: "Gestión de residuos", desc: "Clasificamos y llevamos los residuos a puntos autorizados. Lo que tiene valor se dona o recicla." },
  { num: "06", title: "Entrega y seguimiento", desc: "Te entregamos el inmueble vacío y limpio. Si necesitas pintura final, también lo gestionamos." },
];

function FadeIn({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
    >
      {children}
    </div>
  );
}

export default function EmpresaPage() {
  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <PageSEO
        title="Empresa de Vaciado de Pisos en Girona | GIROBUIT — Quiénes Somos"
        description="Conoce GIROBUIT, empresa especializada en vaciado de pisos y locales en Girona desde 2014. Equipo profesional, discreto y comprometido con la gestión responsable de residuos."
        canonical="https://girobuit.com/empresa"
        pageType="AboutPage"
        breadcrumbs={[
          { name: "Inicio", url: "https://girobuit.com/" },
          { name: "Empresa", url: "https://girobuit.com/empresa" },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[420px] flex items-center justify-center overflow-hidden pt-20">
        <img
          src="https://public.readdy.ai/ai/img_res/0b954e1e618385240278cc4381d09ade.jpg"
          alt="Equipo GIROBUIT - empresa de vaciado de pisos en Girona"
          className="absolute inset-0 w-full h-full object-cover object-top"
          width={1920}
          height={700}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[#330000]/75"></div>
        <div className="relative z-10 text-center px-4 pt-4">
          <nav className="text-xs text-red-300 mb-4">
            <Link to="/" className="hover:text-white cursor-pointer">Inicio</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Empresa</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-900 text-white" style={{ fontWeight: 900 }}>
            Quiénes somos
          </h1>
          <p className="text-red-200 mt-3 text-lg max-w-xl mx-auto">
            El equipo de vaciado de pisos y locales de confianza en Girona
          </p>
        </div>
      </section>

      {/* Historia */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <span className="inline-block text-xs font-700 text-[#ff0000] uppercase tracking-widest mb-3" style={{ fontWeight: 700 }}>
                Nuestra historia
              </span>
              <h2 className="text-3xl md:text-4xl font-800 text-[#330000] mb-6" style={{ fontWeight: 800 }}>
                Más de una década vaciando pisos en Girona con profesionalidad
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4 text-base">
                GIROBUIT nació hace más de 12 años con una misión clara: ofrecer un servicio de <strong>vaciado de pisos y locales en Girona</strong> que fuera realmente de confianza. Un servicio donde el cliente no tuviera que preocuparse de nada: ni del precio, ni de los residuos, ni de la discreción.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4 text-base">
                Empezamos con un equipo pequeño y mucha ilusión. Con el tiempo, la confianza de nuestros clientes en el Barri Vell, Eixample, Montilivi y toda la comarca del Gironès nos fue haciendo crecer. Hoy somos el referente local en vaciados, limpiezas extremas y preparación de inmuebles para la venta o el alquiler.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6 text-base">
                Hemos trabajado con particulares, agencias inmobiliarias, administradores de fincas y servicios sociales. Cada caso es diferente, y nosotros nos adaptamos a cada situación con la misma dedicación y respeto.
              </p>
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 bg-[#ff0000] text-white px-7 py-3.5 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-[#cc0000] hover:scale-105"
                style={{ fontWeight: 700 }}
              >
                Ver nuestros servicios <i className="ri-arrow-right-line"></i>
              </Link>
            </FadeIn>
            <FadeIn>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20clearance%20workers%20loading%20furniture%20into%20a%20van%20in%20a%20Spanish%20city%20street%2C%20sunny%20day%2C%20organized%20team%2C%20photorealistic%2C%20high%20quality%2C%20warm%20tones%2C%20urban%20background%2C%20professional%20service&width=700&height=560&seq=empresa002&orientation=portrait"
                alt="GIROBUIT equipo cargando muebles en furgoneta en Girona"
                className="w-full h-[460px] object-cover object-top rounded-2xl"
                width={700}
                height={560}
                loading="lazy"
                decoding="async"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 bg-[#f9f5f5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <FadeIn className="text-center mb-14">
            <span className="inline-block text-xs font-700 text-[#ff0000] uppercase tracking-widest mb-3" style={{ fontWeight: 700 }}>
              Lo que nos define
            </span>
            <h2 className="text-3xl md:text-4xl font-800 text-[#330000]" style={{ fontWeight: 800 }}>
              Nuestros valores
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <FadeIn key={v.title}>
                <div className="bg-white rounded-2xl p-8 text-center h-full" style={{ border: "1px solid rgba(255,0,0,0.08)" }}>
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#ff0000]/10 mx-auto mb-5">
                    <i className={`${v.icon} text-2xl text-[#ff0000]`}></i>
                  </div>
                  <h3 className="text-base font-700 text-[#330000] mb-3" style={{ fontWeight: 700 }}>{v.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <FadeIn className="text-center mb-14">
            <span className="inline-block text-xs font-700 text-[#ff0000] uppercase tracking-widest mb-3" style={{ fontWeight: 700 }}>
              Cómo trabajamos
            </span>
            <h2 className="text-3xl md:text-4xl font-800 text-[#330000]" style={{ fontWeight: 800 }}>
              Nuestro proceso de trabajo
            </h2>
            <p className="text-gray-600 mt-4 max-w-xl mx-auto">
              Desde el primer contacto hasta la entrega del inmueble, cada paso está pensado para que tú no tengas que preocuparte de nada.{" "}
              <Link to="/faq" className="text-[#ff0000] hover:underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
                Consulta nuestras preguntas frecuentes
              </Link>{" "}
              si tienes dudas.
            </p>
          </FadeIn>
          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#ff0000]/20 -translate-x-1/2 hidden md:block"></div>
            <div className="flex flex-col gap-8">
              {steps.map((step, i) => (
                <FadeIn key={step.num}>
                  <div className={`flex gap-4 md:gap-0 items-start ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                    <div className={`hidden md:flex flex-1 ${i % 2 === 0 ? "justify-end pr-10" : "justify-start pl-10"}`}>
                      <div className="bg-white rounded-xl p-6 max-w-xs" style={{ border: "1px solid rgba(255,0,0,0.12)" }}>
                        <h3 className="text-base font-700 text-[#330000] mb-2" style={{ fontWeight: 700 }}>{step.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                    <div className="relative z-10 w-12 h-12 flex items-center justify-center rounded-full bg-[#ff0000] text-white font-800 text-sm flex-shrink-0" style={{ fontWeight: 800 }}>
                      {step.num}
                    </div>
                    <div className="flex-1 md:hidden">
                      <h3 className="text-base font-700 text-[#330000] mb-1" style={{ fontWeight: 700 }}>{step.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                    </div>
                    <div className="hidden md:block flex-1"></div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Colaboraciones */}
      <section className="py-20 bg-[#330000]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <span className="inline-block text-xs font-700 text-[#FF6666] uppercase tracking-widest mb-3" style={{ fontWeight: 700 }}>
                Colaboraciones
              </span>
              <h2 className="text-3xl md:text-4xl font-800 text-white mb-6" style={{ fontWeight: 800 }}>
                Trabajamos con agencias inmobiliarias y administradores de fincas
              </h2>
              <p className="text-red-200 leading-relaxed mb-4">
                Si eres agente inmobiliario o administrador de fincas en Girona, sabemos lo importante que es tener un servicio de vaciado de pisos rápido, fiable y discreto. Somos tu aliado para preparar inmuebles para la venta o el alquiler.
              </p>
              <p className="text-red-200 leading-relaxed mb-6">
                Ofrecemos tarifas especiales para colaboradores habituales, respuesta prioritaria y total confidencialidad en cada encargo. Muchas de las agencias más activas en Girona, Salt y Banyoles ya confían en nosotros.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <a
                  href="tel:+34602365996"
                  className="flex items-center justify-center gap-2 bg-[#ff0000] text-white px-7 py-3.5 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-[#cc0000] w-full sm:w-auto"
                  style={{ fontWeight: 700 }}
                >
                  <i className="ri-phone-fill"></i> Hablar con nosotros
                </a>
                <Link
                  to="/contacto"
                  className="flex items-center justify-center gap-2 border-2 border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer transition-all duration-300 hover:border-white/60 w-full sm:w-auto"
                  style={{ fontWeight: 700 }}
                >
                  <i className="ri-mail-line"></i> Enviar consulta
                </Link>
              </div>
            </FadeIn>
            <FadeIn>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: "ri-building-line", title: "Agencias inmobiliarias", desc: "Preparamos pisos para la venta o el alquiler con rapidez y eficiencia." },
                  { icon: "ri-key-2-line", title: "Administradores de fincas", desc: "Gestionamos vaciados de pisos de herencias, desahucios y cambios de inquilino." },
                  { icon: "ri-hospital-line", title: "Servicios sociales", desc: "Colaboramos con servicios sociales en casos de síndrome de Diógenes y situaciones vulnerables." },
                  { icon: "ri-home-gear-line", title: "Particulares", desc: "Ayudamos a familias a gestionar herencias, mudanzas y vaciados de forma sencilla." },
                ].map((item) => (
                  <div key={item.title} className="bg-white/10 rounded-xl p-5 border border-white/10">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#ff0000]/30 mb-3">
                      <i className={`${item.icon} text-lg text-[#FF6666]`}></i>
                    </div>
                    <h3 className="text-sm font-700 text-white mb-1.5" style={{ fontWeight: 700 }}>{item.title}</h3>
                    <p className="text-xs text-red-300 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FF6666]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-800 text-[#330000] mb-4" style={{ fontWeight: 800 }}>
            ¿Listo para trabajar con nosotros?
          </h2>
          <p className="text-[#330000]/80 mb-8 text-base">
            Pide tu presupuesto sin compromiso. Atendemos en Girona y toda la comarca del Gironès.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+34602365996"
              className="flex items-center justify-center gap-2 bg-[#330000] text-white px-8 py-4 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-[#ff0000]"
              style={{ fontWeight: 700 }}
            >
              <i className="ri-phone-fill"></i> 602 36 59 96
            </a>
            <Link
              to="/contacto"
              className="flex items-center justify-center gap-2 border-2 border-[#330000] text-[#330000] px-8 py-4 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-[#330000] hover:text-white"
              style={{ fontWeight: 700 }}
            >
              <i className="ri-mail-line"></i> Formulario de contacto
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
